import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import {AlertTriangleIcon, ArrowDownIcon, ArrowUpIcon, GripVerticalIcon, PencilIcon, PlusIcon, Trash2Icon, UserIcon} from "lucide-react";
import Button from "../../../../../components/Button";
import SectionHeading from "../../../../../components/SectionHeading";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {formatProgrammeDate, formatSlotRange, isoToWindhoekInput, joinRoles, kindLabel, roleLabel} from "../../../../../lib/eventProgramme";
import type {EventDayResponse, EventLineupSlot, EventSpeaker} from "../../../../../types/event";
import SpeakerEditor, {SpeakerDraft, speakerDto} from "../components/SpeakerEditor";
import LineupSlotEditor, {SlotDraft, slotRequest} from "../components/LineupSlotEditor";

type OutletCtx = { eventId: string | null };

type Editing =
    | { type: "speaker"; speaker: EventSpeaker | null }
    | { type: "slot"; slot: EventLineupSlot | null }
    | null;

const rowBtn = "rounded-lg border border-gray-200 bg-white p-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40";
const editBtn =
    "inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40";
const deleteBtn =
    "inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-40";

function moveItem<T>(arr: T[], from: number, to: number): T[] {
    const copy = [...arr];
    const [x] = copy.splice(from, 1);
    copy.splice(to, 0, x);
    return copy;
}

/**
 * Speakers and the line-up for an event. Every write answers with the whole list for that resource
 * in its new order, which replaces local state. Overlap warnings from the backend are shown on the
 * slots and never block a save.
 */
export function SpeakersStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const base = `/api/v2/business/${businessId}/events/${eventId}`;

    const [speakers, setSpeakers] = useState<EventSpeaker[]>([]);
    const [slots, setSlots] = useState<EventLineupSlot[]>([]);
    const [days, setDays] = useState<EventDayResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState<Editing>(null);

    const dragFrom = useRef<number | null>(null);

    const loadSlots = useCallback(async () => {
        const {data} = await api.get(`${base}/lineup`);
        setSlots(Array.isArray(data) ? data : []);
    }, [base]);

    useEffect(() => {
        if (!businessId || !eventId) return;
        setLoading(true);
        Promise.all([
            api.get(`${base}/speakers`).then(({data}) => setSpeakers(Array.isArray(data) ? data : [])),
            loadSlots(),
            api.get(`/api/v1/business/${businessId}/events/${eventId}/days`)
                .then(({data}) => setDays(Array.isArray(data) ? data : []))
                .catch(() => setDays([])), // the day picker is optional; a slot can go without one
        ])
            .catch((e) => notify.error(getErrMsg(e, "Failed to load speakers and line-up")))
            .finally(() => setLoading(false));
    }, [base, businessId, eventId, loadSlots]);

    const run = async <T, >(action: () => Promise<{ data: unknown }>, apply: (list: T[]) => void,
                            success: string, failure: string) => {
        setBusy(true);
        try {
            const {data} = await action();
            const list = (Array.isArray(data) ? data : []) as T[];
            apply(list);
            notify.success(success);
            return list;
        } catch (e) {
            notify.error(getErrMsg(e, failure));
            return null;
        } finally {
            setBusy(false);
        }
    };

    const saveSpeaker = async (draft: SpeakerDraft, file: File | null, removeImage: boolean) => {
        if (editing?.type !== "speaker") return;
        const current = editing.speaker;
        const fd = new FormData();
        fd.append("dto", new Blob([JSON.stringify(speakerDto(draft, removeImage))], {type: "application/json"}));
        if (file) fd.append("file", file);
        const cfg = {headers: {"Content-Type": "multipart/form-data"}};
        const ok = await run<EventSpeaker>(
            () => current ? api.put(`${base}/speakers/${current.id}`, fd, cfg) : api.post(`${base}/speakers`, fd, cfg),
            setSpeakers, "Speaker saved", "Failed to save speaker",
        );
        if (!ok) return;
        setEditing(null);
        // slot cards carry the speaker's name and photo
        if (current) loadSlots().catch(() => undefined);
    };

    const reorderSpeakers = (from: number, to: number) => {
        if (to < 0 || to >= speakers.length || from === to) return;
        const previous = speakers;
        const moved = moveItem(speakers, from, to);
        setSpeakers(moved);
        void run<EventSpeaker>(() => api.put(`${base}/speakers/order`, {speakerIds: moved.map((s) => s.id)}),
            setSpeakers, "Speaker order saved", "Failed to save speaker order")
            .then((ok) => {
                if (!ok) setSpeakers(previous);
            });
    };

    const deleteSpeaker = async (s: EventSpeaker) => {
        if (!confirm(`Delete speaker "${s.name}"? They are also removed from every line-up slot.`)) return;
        const ok = await run<EventSpeaker>(() => api.delete(`${base}/speakers/${s.id}`),
            setSpeakers, "Speaker deleted", "Failed to delete speaker");
        if (ok) loadSlots().catch(() => undefined);
    };

    const saveSlot = async (draft: SlotDraft) => {
        if (editing?.type !== "slot") return;
        const current = editing.slot;
        const body = slotRequest(draft);
        const list = await run<EventLineupSlot>(
            () => current ? api.put(`${base}/lineup/${current.id}`, body) : api.post(`${base}/lineup`, body),
            setSlots, "Slot saved", "Failed to save slot",
        );
        if (!list) return;
        setEditing(null);
        if (list.some((s) => (s.overlapsWith ?? []).length > 0)) {
            notify.info("Some slots overlap on the same stage. Check the warnings in the line-up.");
        }
    };

    const deleteSlot = (s: EventLineupSlot) => {
        if (!confirm(`Delete slot "${s.title}"?`)) return;
        void run<EventLineupSlot>(() => api.delete(`${base}/lineup/${s.id}`), setSlots, "Slot deleted", "Failed to delete slot");
    };

    // business list arrives flat in time order; group it by event day, else by Windhoek date
    const groups = useMemo(() => {
        const out: Array<{ key: string; label: string; slots: EventLineupSlot[] }> = [];
        slots.forEach((s) => {
            const day = s.eventDayId ? days.find((d) => d.id === s.eventDayId) : undefined;
            const date = isoToWindhoekInput(s.startsAt).slice(0, 10);
            const key = s.eventDayId ?? `date-${date}`;
            const label = day?.name ?? (date ? formatProgrammeDate(date) : "Unscheduled");
            const g = out.find((x) => x.key === key);
            if (g) g.slots.push(s);
            else out.push({key, label, slots: [s]});
        });
        return out;
    }, [slots, days]);

    const titleOf = (id: string) => slots.find((s) => s.id === id)?.title ?? "another slot";

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/feedback`);
        else navigate(`/business/events/new/feedback?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    if (editing?.type === "speaker") {
        return <SpeakerEditor speaker={editing.speaker} saving={busy} onSave={saveSpeaker} onCancel={() => setEditing(null)}/>;
    }
    if (editing?.type === "slot") {
        return (
            <LineupSlotEditor slot={editing.slot} days={days} speakers={speakers} saving={busy}
                              onSave={saveSlot} onCancel={() => setEditing(null)}/>
        );
    }

    return (
        <div className="space-y-6">
            <SectionHeading
                title={mode === "edit" ? "Edit speakers and line-up" : "Add speakers and line-up"}
                description="Speakers and the programme appear on the event page. Both sections stay hidden until they have entries."
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading speakers and line-up…</div>
            ) : (
                <>
                    <section aria-labelledby="speakers-heading" className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                            <h3 id="speakers-heading" className="text-sm font-semibold text-gray-900">
                                Speakers ({speakers.length})
                            </h3>
                            <Button type="button" variant="primary" size="sm" disabled={busy}
                                    onClick={() => setEditing({type: "speaker", speaker: null})}
                                    leftIcon={<PlusIcon className="size-4" aria-hidden="true"/>}>
                                Add speaker
                            </Button>
                        </div>
                        {speakers.length === 0 ? (
                            <p className="px-5 py-10 text-center text-sm text-gray-500">
                                No speakers yet. Click <span className="font-medium">Add speaker</span>.
                            </p>
                        ) : (
                            <ul className="divide-y divide-gray-100">
                                {speakers.map((s, idx) => (
                                    <li
                                        key={s.id}
                                        draggable={!busy}
                                        onDragStart={() => (dragFrom.current = idx)}
                                        onDragOver={(e: React.DragEvent) => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = "move";
                                        }}
                                        onDrop={() => {
                                            const from = dragFrom.current;
                                            dragFrom.current = null;
                                            if (from != null) reorderSpeakers(from, idx);
                                        }}
                                        className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center"
                                    >
                                        <div className="flex min-w-0 flex-1 items-center gap-3">
                                            <GripVerticalIcon className="size-4 shrink-0 text-gray-400" aria-hidden="true"/>
                                            <div className="size-10 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-black/5">
                                                {s.imageUrl ? (
                                                    <img src={s.imageUrl} alt="" className="size-10 object-cover"/>
                                                ) : (
                                                    <UserIcon className="m-2.5 size-5 text-gray-400" aria-hidden="true"/>
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-semibold text-gray-900">{s.name}</div>
                                                <div className="truncate text-xs text-gray-500">
                                                    {[joinRoles(s.roles), s.position, s.organisation].filter(Boolean).join(" · ")}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button type="button" className={rowBtn} disabled={busy || idx === 0}
                                                    onClick={() => reorderSpeakers(idx, idx - 1)} aria-label={`Move ${s.name} up`}>
                                                <ArrowUpIcon className="size-4"/>
                                            </button>
                                            <button type="button" className={rowBtn} disabled={busy || idx === speakers.length - 1}
                                                    onClick={() => reorderSpeakers(idx, idx + 1)} aria-label={`Move ${s.name} down`}>
                                                <ArrowDownIcon className="size-4"/>
                                            </button>
                                            <button type="button" className={editBtn} disabled={busy}
                                                    onClick={() => setEditing({type: "speaker", speaker: s})} aria-label={`Edit ${s.name}`}>
                                                <PencilIcon className="size-4"/>
                                                <span className="hidden sm:inline" aria-hidden="true">Edit</span>
                                            </button>
                                            <button type="button" className={deleteBtn} disabled={busy}
                                                    onClick={() => deleteSpeaker(s)} aria-label={`Delete ${s.name}`}>
                                                <Trash2Icon className="size-4"/>
                                                <span className="hidden sm:inline" aria-hidden="true">Delete</span>
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <section aria-labelledby="lineup-heading" className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                            <h3 id="lineup-heading" className="text-sm font-semibold text-gray-900">
                                Line-up ({slots.length} {slots.length === 1 ? "slot" : "slots"})
                            </h3>
                            <Button type="button" variant="primary" size="sm" disabled={busy}
                                    onClick={() => setEditing({type: "slot", slot: null})}
                                    leftIcon={<PlusIcon className="size-4" aria-hidden="true"/>}>
                                Add slot
                            </Button>
                        </div>
                        {groups.length === 0 ? (
                            <p className="px-5 py-10 text-center text-sm text-gray-500">
                                No slots yet. Click <span className="font-medium">Add slot</span>.
                            </p>
                        ) : groups.map((g) => (
                            <div key={g.key}>
                                <h4 className="border-b border-gray-100 px-5 pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {g.label}
                                </h4>
                                <ul className="divide-y divide-gray-100">
                                    {g.slots.map((s) => {
                                        const overlaps = s.overlapsWith ?? [];
                                        return (
                                            <li key={s.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
                                                <div className="w-28 shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                                                    {formatSlotRange(s.startsAt, s.endsAt)}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-semibold text-gray-900">{s.title}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {[kindLabel(s.kind), s.stage].filter(Boolean).join(" · ")}
                                                    </div>
                                                    {s.speakers.length ? (
                                                        <div className="mt-1 text-xs text-gray-700">
                                                            {s.speakers.map((x) => `${x.name} (${roleLabel(x.role)})`).join(", ")}
                                                        </div>
                                                    ) : null}
                                                    {overlaps.length ? (
                                                        <p className="mt-2 inline-flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-800 ring-1 ring-amber-200">
                                                            <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true"/>
                                                            <span>
                                                                <span className="sr-only">Warning: </span>
                                                                Overlaps on the same stage with {overlaps.map(titleOf).join(", ")}.
                                                            </span>
                                                        </p>
                                                    ) : null}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button type="button" className={editBtn} disabled={busy}
                                                            onClick={() => setEditing({type: "slot", slot: s})} aria-label={`Edit ${s.title}`}>
                                                        <PencilIcon className="size-4"/>
                                                        <span className="hidden sm:inline" aria-hidden="true">Edit</span>
                                                    </button>
                                                    <button type="button" className={deleteBtn} disabled={busy}
                                                            onClick={() => deleteSlot(s)} aria-label={`Delete ${s.title}`}>
                                                        <Trash2Icon className="size-4"/>
                                                        <span className="hidden sm:inline" aria-hidden="true">Delete</span>
                                                    </button>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </section>
                </>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md" onClick={() => navigate("/business/events")}>
                    Exit
                </Button>
                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={busy}>
                    Continue to Feedback
                </Button>
            </div>
        </div>
    );
}
